import React, { useState } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, FileSpreadsheet, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOWNLOAD_CATEGORIES = [
  {
    category: 'Productivity',
    items: [
      { key: 'smart-workflows', name: 'Smart Workflows', description: 'Workflows with steps and execution history' },
      { key: 'projects', name: 'Projects', description: 'Projects with tasks and milestones' },
    ],
  },
  {
    category: 'Masters',
    items: [
      { key: 'item-master', name: 'Item Master', description: 'Complete item master with stock levels and classifications' },
      { key: 'uom-master', name: 'UOM Master', description: 'Unit of measurement master list' },
      { key: 'bom-master', name: 'BOM Master', description: 'Bill of materials with processes and materials (one row per material)' },
      { key: 'variant-groups', name: 'Variant Groups', description: 'Variant groups with linked items (one row per item)' },
      { key: 'client-master', name: 'Client Master', description: 'Client master with delivery addresses (one row per address)' },
      { key: 'process-master', name: 'Process Master', description: 'Process master with sub-processes (one row per sub-process)' },
      { key: 'supplier-master', name: 'Supplier Master', description: 'Supplier master with contact and payment details' },
    ],
  },
  {
    category: 'Inventory',
    items: [
      { key: 'item-code-constructor', name: 'Item Code Constructor', description: 'Product masters and generated item codes (2 sheets)' },
    ],
  },
  {
    category: 'Sales',
    items: [
      { key: 'sales-orders', name: 'Sales Orders', description: 'All sales orders with line items (one row per item)' },
      { key: 'variants-pending', name: 'Variants Pending', description: 'Items with pending variant selections' },
      { key: 'so-fulfillment', name: 'SO Fulfillment', description: 'SO fulfillment with WO status and dispatch tracking' },
      { key: 'item-code-manager', name: 'Item Code Manager', description: 'Item code requests and approvals' },
    ],
  },
  {
    category: 'Production',
    items: [
      { key: 'pending-bom', name: 'Pending BOM Register', description: 'Sales orders pending BOM creation' },
      { key: 'production-fulfillment', name: 'Production Fulfillment', description: 'Production status with WO and material tracking' },
      { key: 'work-orders', name: 'Work Orders', description: 'All work orders with status and completion details' },
      { key: 'ojc-register', name: 'OJC Register', description: 'Outside job card register with dispatch history' },
      { key: 'complaints', name: 'Complaints Dashboard', description: 'Complaints with corrective and preventive action tracking' },
    ],
  },
  {
    category: 'Purchase',
    items: [
      { key: 'indents', name: 'Indent Register', description: 'All indents with details and approval status' },
      { key: 'purchase-orders', name: 'Purchase Orders', description: 'All POs with line items and delivery tracking' },
      { key: 'service-po', name: 'Service PO', description: 'Service purchase orders with completion status' },
      { key: 'procurement-tracking', name: 'Procurement Tracking', description: 'PO delivery tracking with pending quantities and delays' },
      { key: 'purchase-entry', name: 'Purchase Entry', description: 'Purchase entries/GRN with supplier vs system pricing' },
      { key: 'service-invoices', name: 'Service Invoices (SRN)', description: 'Services received notes with service items' },
      { key: 'purchase-fulfillment', name: 'Purchase Fulfillment', description: 'Indent to PO to receipt chain tracking' },
      { key: 'payment-requests', name: 'Payment Requests', description: 'Payment requests with approval and payment status' },
      { key: 'invoice-report', name: 'Invoice Report', description: 'Invoice and document handover tracking' },
      { key: 'grn-register', name: 'GRN Register', description: 'Store receipts with item and quality details' },
    ],
  },
  {
    category: 'Stores',
    items: [
      { key: 'gate-entries', name: 'Gate Entry Register', description: 'Gate entry register with supplier and vehicle details' },
      { key: 'gate-passes', name: 'Gate Pass', description: 'Gate passes with items and return tracking' },
      { key: 'delivery-challans', name: 'Delivery Challan', description: 'Delivery challans with items and dispatch details' },
      { key: 'store-receiving', name: 'Store Receiving', description: 'Store receipts with quality inspection details' },
      { key: 'pending-issues', name: 'Pending Issues', description: 'Pending bin card demands and material issues' },
      { key: 'bin-card', name: 'Bin Card', description: 'Bin card stock vs stock in hand tracking' },
      { key: 'ctm-issuance', name: 'CTM Issuance', description: 'Cut to measure issuance records' },
      { key: 'inter-unit-transfers', name: 'Inter Unit Transfers', description: 'Inter unit transfers with items and receipt status' },
    ],
  },
  {
    category: 'Accounts',
    items: [
      { key: 'record-payments', name: 'Record Payments', description: 'Bank transactions and payment records with line items' },
      { key: 'grn-documents', name: 'GRN Documents', description: 'GRN document tracking for accounts team' },
    ],
  },
];

export default function DownloadsPage() {
  const [downloading, setDownloading] = useState({});
  const [completed, setCompleted] = useState({});

  const handleDownload = async (downloadKey) => {
    setDownloading(prev => ({ ...prev, [downloadKey]: true }));
    setCompleted(prev => ({ ...prev, [downloadKey]: false }));

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/downloads/${downloadKey}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${downloadKey}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setCompleted(prev => ({ ...prev, [downloadKey]: true }));
      toast.success(`Downloaded ${filename}`);
      
      // Reset completed status after 3 seconds
      setTimeout(() => {
        setCompleted(prev => ({ ...prev, [downloadKey]: false }));
      }, 3000);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download file');
    } finally {
      setDownloading(prev => ({ ...prev, [downloadKey]: false }));
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="downloads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Downloads</h1>
          <p className="text-sm text-gray-500 mt-1">
            Export data to Excel files for reporting and analysis
          </p>
        </div>
      </div>

      <div className="space-y-8">
        {DOWNLOAD_CATEGORIES.map((cat) => (
          <div key={cat.category}>
            <h2 className="text-lg font-semibold text-gray-800 mb-3" data-testid={`download-category-${cat.category.toLowerCase()}`}>
              {cat.category}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {cat.items.map((option) => {
                const isDownloading = downloading[option.key];
                const isCompleted = completed[option.key];

                return (
                  <Card 
                    key={option.key} 
                    className="hover:shadow-md transition-shadow"
                    data-testid={`download-card-${option.key}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                        </div>
                        <CardTitle className="text-base">{option.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="text-sm mb-4 min-h-[40px]">
                        {option.description}
                      </CardDescription>
                      <Button
                        onClick={() => handleDownload(option.key)}
                        disabled={isDownloading}
                        className="w-full"
                        variant={isCompleted ? "outline" : "default"}
                        data-testid={`download-btn-${option.key}`}
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : isCompleted ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                            Downloaded
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download Excel
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
        <ul className="text-sm text-gray-500 space-y-1">
          <li>• All exports include complete data without any filters applied</li>
          <li>• For registers with line items (SO, PO, Indents), each line item is exported as a separate row</li>
          <li>• Files are generated in real-time with the latest data</li>
        </ul>
      </div>
    </div>
    </DashboardLayout>
  );
}
